
import { Component, Input, Output, OnInit, EventEmitter, Optional, Inject, HostListener, ViewChild, ElementRef, OnChanges } from '@angular/core';

import { ImageViewerConfig, CustomEvent } from './image-viewer-config.model';
import { PRIMENG_MODULES } from '../../primeng-imports';

const DEFAULT_CONFIG: ImageViewerConfig = {
  btnClass: 'default',
  zoomFactor: 0.1,
  containerBackgroundColor: '#ccc',
  wheelZoom: false,
  allowFullscreen: true,
  allowKeyboardNavigation: true,
  btnShow: {
    zoomIn: true,
    zoomOut: true,
    rotateClockwise: true,
    rotateCounterClockwise: true,
    next: true,
    prev: true
  },
  btnIcons: {
    zoomIn: 'pi pi-plus',
    zoomOut: 'pi pi-minus',
    rotateClockwise: 'pi pi-repeat',
    rotateCounterClockwise: 'pi pi-undo',
    next: 'pi pi-arrow-right',
    prev: 'pi pi-arrow-left',
    fullscreen: 'pi pi-arrows-alt',
  }
};
@Component({
  selector: 'app-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss'],
  standalone:true,
  imports:[...PRIMENG_MODULES]
})
export class ImageViewerComponent implements OnInit, OnChanges {

  @Input() src: string[] = [];

  @Input() index = 0;
  @Input() fromTab = 'STab';
  @Input() fileName = 'zoomed-image.png';

  @Input() config: ImageViewerConfig = {} as ImageViewerConfig;

  @Output()
  indexChange: EventEmitter<number> = new EventEmitter();

  @Output()
  configChange: EventEmitter<ImageViewerConfig> = new EventEmitter();

  @Output()
  customEvent: EventEmitter<CustomEvent> = new EventEmitter();

  @ViewChild('imgRef', { static: false }) imgRef!: ElementRef<HTMLImageElement>;

  public style = { transform: '', msTransform: '', oTransform: '', webkitTransform: '' };
  public fullscreen = false;
  public loading = true;
  private scale = 1;
  private rotation = 0;
  private translateX = 0;
  private translateY = 0;
  private prevX!: number;
  private prevY!: number;
  public panning = false;
  private activePointerId: number | null = null;
  private hovered = false;
  showRerenderedImageDownload = false;
  private canvasImg!: HTMLImageElement;
  imageReady: boolean = false;
  canvasImageIsOriginClean = false;
  constructor(@Optional() @Inject('config') public moduleConfig: ImageViewerConfig) { }

  ngOnInit() {
    const merged = this.mergeConfig(DEFAULT_CONFIG, this.moduleConfig);
    this.config = this.mergeConfig(merged, this.config);
    this.triggerConfigBinding();

    this.config.wheelZoom = true;
    this.preloadImage()
  }

  @HostListener('window:keyup.ArrowRight', ['$event'])
  nextImage(event: any) {
    if (this.canNavigate(event) && this.index < this.src.length - 1) {
      this.loading = true;
      this.index++;
      this.triggerIndexBinding();
      this.reset();
    }
  }

  @HostListener('window:keyup.ArrowLeft', ['$event'])
  prevImage(event: any) {
    if (this.canNavigate(event) && this.index > 0) {
      this.loading = true;
      this.index--;
      this.triggerIndexBinding();
      this.reset();
    }
  }

  zoomIn() {
    if (this.config.zoomFactor)
      this.scale *= (1 + this.config.zoomFactor);
    this.updateStyle();
  }

  zoomOut() {
    if (this.config.zoomFactor)
      if (this.scale > this.config.zoomFactor) {
        this.scale /= (1 + this.config.zoomFactor);
      }
    this.updateStyle();
  }

  scrollZoom(evt: any): boolean {
    let sonuc: boolean = true;

    if (this.config.wheelZoom) {
      evt.deltaY > 0 ? this.zoomOut() : this.zoomIn();
      sonuc = false;
    }

    return sonuc;
  }

  rotateClockwise() {
    this.rotation += 90;
    this.updateStyle();
  }

  rotateCounterClockwise() {
    this.rotation -= 90;
    this.updateStyle();
  }

  onLoad() {
    this.loading = false;
  }

  onLoadStart() {
    this.loading = true;
  }

  onPointerDown(evt: PointerEvent) {
    if (evt.button !== 0) {
      return;
    }

    this.panning = true;
    this.activePointerId = evt.pointerId;
    this.prevX = evt.clientX;
    this.prevY = evt.clientY;
    this.imgRef?.nativeElement.setPointerCapture(evt.pointerId);
    evt.preventDefault();
  }

  onPointerMove(evt: PointerEvent) {
    if (!this.panning || this.activePointerId !== evt.pointerId) {
      return;
    }

    this.translateX += (evt.clientX - this.prevX);
    this.translateY += (evt.clientY - this.prevY);
    this.prevX = evt.clientX;
    this.prevY = evt.clientY;
    this.updateStyle();
    evt.preventDefault();
  }

  onPointerUp(evt: PointerEvent) {
    if (this.activePointerId !== evt.pointerId) {
      return;
    }

    if (this.imgRef?.nativeElement.hasPointerCapture(evt.pointerId)) {
      this.imgRef.nativeElement.releasePointerCapture(evt.pointerId);
    }

    this.panning = false;
    this.activePointerId = null;
  }

  toggleFullscreen() {
    this.fullscreen = !this.fullscreen;
    if (!this.fullscreen) {
      this.reset();
    }
  }

  triggerIndexBinding() {
    this.indexChange.emit(this.index);
  }

  triggerConfigBinding() {
    this.configChange.next(this.config);
  }

  fireCustomEvent(name: any, imageIndex: any) {
    this.customEvent.emit(new CustomEvent(name, imageIndex));
  }

  reset() {
    this.scale = 1;
    this.rotation = 0;
    this.translateX = 0;
    this.translateY = 0;
    this.updateStyle();
    this.showRerenderedImageDownload = false;
  }

  @HostListener('mouseover')
  onMouseOver() {
    this.hovered = true;
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    this.hovered = false;
  }

  private canNavigate(event: any) {
    return event == null || (this.config.allowKeyboardNavigation && this.hovered);
  }

  private updateStyle() {
    if (this.fromTab == 'STab')
      this.showRerenderedImageDownload = true;
    this.style.transform = `translate(${this.translateX}px, ${this.translateY}px) rotate(${this.rotation}deg) scale(${this.scale})`;
    this.style.msTransform = this.style.transform;
    this.style.webkitTransform = this.style.transform;
    this.style.oTransform = this.style.transform;
  }

  private mergeConfig(defaultValues: ImageViewerConfig, overrideValues: ImageViewerConfig): ImageViewerConfig {
    let result: ImageViewerConfig = { ...defaultValues };
    if (overrideValues) {
      result = { ...defaultValues, ...overrideValues };

      if (overrideValues.btnIcons) {
        result.btnIcons = { ...defaultValues.btnIcons, ...overrideValues.btnIcons };
      }
    }
    return result;
  }
  preloadImage() {
    const loadImage = (withCors: boolean) => {
      const img = new Image();
      if (withCors) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        this.canvasImg = img;
        this.imageReady = true;
        this.canvasImageIsOriginClean = withCors;
      };

      img.onerror = (error) => {
        if (withCors) {
          loadImage(false);
        } else {
          console.error('Image preload failed', error);
        }
      };

      img.src = this.src[this.index];
    };

    this.imageReady = false;
    this.canvasImageIsOriginClean = false;
    loadImage(true);
  }

  downloadCanvasImage() {
    if (!this.imageReady || !this.canvasImageIsOriginClean) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    const outputScale = window.devicePixelRatio || 1;
    canvas.width = this.canvasImg.naturalWidth * outputScale;
    canvas.height = this.canvasImg.naturalHeight * outputScale;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.scale(this.scale, this.scale);
    ctx.rotate((this.rotation * Math.PI) / 180);
    ctx.translate(this.translateX, this.translateY);

    ctx.drawImage(
      this.canvasImg,
      -this.canvasImg.naturalWidth / 2,
      -this.canvasImg.naturalHeight / 2
    );

    const link = document.createElement('a');
    link.download = this.fileName;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }
  ngOnChanges() {
    this.preloadImage();
    this.reset()
  }
}